/**
 * LicenseFeesBox — receipt upload + amount/reference + paid_to for the
 * license fee.
 *
 * iter-264e (Feb 2026) — Per user request each fee row now carries an
 * explicit paid_to tag:
 *   • مسدد لجهة حكومية (to_gov) — customer pays the government directly
 *   • مسدد لنا         (to_us)  — customer pays us; forwarded/kept.
 * The fee-collection card (سداد الرسوم) reads this to compute the
 * amount owed to us vs the amount that bypassed us.
 */
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { IdCard, Loader2, Upload, Landmark, Wallet } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { SectionCard, SmartSaveButton, ReceiptDownloadLink } from './ui';
import { fileToDataUrl, extractReceipt } from './helpers';

export function LicenseFeesBox({ reg, onSave, t, saving }) {
  const l = reg.license_fees || {};
  const [amount, setAmount] = useState(l.amount || '');
  const [reference, setReference] = useState(l.reference_number || '');
  const [receiptUrl, setReceiptUrl] = useState(l.receipt_url || '');
  const [receiptMime, setReceiptMime] = useState(l.receipt_mime || '');
  const [aiVerified, setAiVerified] = useState(!!l.ai_verified);
  const [scanning, setScanning] = useState(false);
  const [aiInfo, setAiInfo] = useState('');
  // iter-264e — paid_to selector state ('to_gov' | 'to_us' | null).
  const [paidTo, setPaidTo] = useState(l.paid_to || null);

  const dirty = useMemo(() => (
    String(amount || '') !== String(l.amount || '') ||
    (reference || '') !== (l.reference_number || '') ||
    (receiptUrl || '') !== (l.receipt_url || '') ||
    (receiptMime || '') !== (l.receipt_mime || '') ||
    !!aiVerified !== !!l.ai_verified ||
    (paidTo || null) !== (l.paid_to || null)
  ), [amount, reference, receiptUrl, receiptMime, aiVerified, paidTo,
      l.amount, l.reference_number, l.receipt_url, l.receipt_mime, l.ai_verified, l.paid_to]);

  const handleReceipt = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setReceiptUrl(url);
    setReceiptMime(file.type);
    setScanning(true);
    setAiInfo('');
    setAiVerified(false);
    try {
      const data = await extractReceipt(url, file.type, amount ? parseFloat(amount) : null, 'license_fees');
      if (!data.is_receipt) {
        setAiInfo(t('❌ الملف ليس فاتورة/إيصال رسوم رخصة: ', '❌ Not a license-fee invoice/receipt: ') + (data.reject_reason || ''));
        setAiVerified(false);
        toast.error(t('الملف ليس فاتورة/إيصال رسوم رخصة', 'Not a license-fee invoice/receipt'));
      } else {
        if (data.amount) setAmount(String(data.amount));
        if (data.reference) setReference(String(data.reference));
        setAiVerified(true);
        setAiInfo(t('✓ AI قرأ الفاتورة/الإيصال', '✓ AI read the invoice/receipt'));
        toast.success(t('✓ تم قراءة الفاتورة/الإيصال', '✓ Invoice/receipt scanned'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل قراءة الإيصال', 'OCR failed'));
    } finally {
      setScanning(false);
    }
  };

  const save = () => {
    if (!receiptUrl) {
      toast.error(t('فاتورة/إيصال الرخصة مطلوب', 'License invoice/receipt required'));
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('المبلغ مطلوب', 'Amount required'));
      return;
    }
    if (!reference || !reference.trim()) {
      toast.error(t('رقم المرجع مطلوب', 'Reference required'));
      return;
    }
    if (!paidTo) {
      toast.error(t('اختر: مسدد لجهة حكومية أو مسدد لنا', 'Pick: Paid to government or Paid to us'));
      return;
    }
    onSave({ license_fees: {
      amount: parseFloat(amount), reference_number: reference,
      receipt_url: receiptUrl, receipt_mime: receiptMime, ai_verified: aiVerified,
      paid_to: paidTo,
    }});
  };

  return (
    <SectionCard icon={IdCard} iconColor="#3b82f6" title={t('رسوم الرخصة', 'License fees')}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('فاتورة/إيصال دفع الرخصة *', 'License invoice/receipt *')}</label>
          <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> : <Upload className="h-4 w-4 text-blue-400" />}
            {scanning ? t('يقرأ AI...', 'AI reading...') : receiptUrl ? t('تم الرفع ✓', 'Uploaded ✓') : t('رفع', 'Upload')}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleReceipt(e.target.files?.[0])} />
          </label>
          {aiInfo ? <p className="text-xs mt-1" style={{ color: aiVerified ? '#10b981' : '#ef4444' }}>{aiInfo}</p> : null}
          {l.receipt_url ? (
            <ReceiptDownloadLink
              url={l.receipt_url}
              filename={`license-receipt-${reg.request_code || reg.id}${(l.receipt_mime || '').includes('pdf') ? '.pdf' : '.jpg'}`}
              label={t('إيصال الرخصة المحفوظ', 'Saved license receipt')}
              color="#3b82f6"
              testId="license-receipt"
            />
          ) : null}
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('المبلغ (د.إ) *', 'Amount (AED) *')}</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="license-fees-amount" />
        </div>
        <div>
          <label className="block text-xs text-[#71717A] mb-2">{t('رقم المرجع *', 'Reference *')}</label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" />
        </div>
      </div>

      {/* iter-264e — paid_to selector */}
      <div className="mt-4">
        <label className="block text-xs text-[#71717A] mb-2">
          {t('طريقة سداد رسوم الرخصة *', 'License fee payment routing *')}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setPaidTo('to_gov')}
            className={
              'p-3 rounded-xl border-2 text-sm font-semibold transition-all flex items-center gap-2 justify-center ' +
              (paidTo === 'to_gov'
                ? 'bg-blue-500/15 border-blue-500 text-blue-300'
                : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
            }
            data-testid="license-paid-to-gov-btn"
          >
            <Landmark className="h-4 w-4" />
            {t('مسدد لجهة حكومية', 'Paid to government')}
          </button>
          <button
            type="button"
            onClick={() => setPaidTo('to_us')}
            className={
              'p-3 rounded-xl border-2 text-sm font-semibold transition-all flex items-center gap-2 justify-center ' +
              (paidTo === 'to_us'
                ? 'bg-emerald-500/15 border-emerald-500 text-emerald-300'
                : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
            }
            data-testid="license-paid-to-us-btn"
          >
            <Wallet className="h-4 w-4" />
            {t('مسدد لنا', 'Paid to us')}
          </button>
        </div>
      </div>

      <div className="mt-4 text-end">
        <SmartSaveButton
          dirty={dirty}
          saving={saving}
          onClick={save}
          t={t}
          color="#3b82f6"
          hoverColor="#2563eb"
          data-testid="license-fees-save"
        />
      </div>
    </SectionCard>
  );
}
